import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { POLL_STALE_MINUTES, POLL_OFFLINE_MINUTES } from '@/lib/poll-status';

export type HealthStatus = 'healthy' | 'attention' | 'issue';

export interface SystemHealthData {
  status: HealthStatus;
  label: string;
  explanation: string;
  isOnline: boolean;
  isCurtailing: boolean;
  isUnderperforming: boolean;
  /** WT-341 v3 honest PV health state (null until compute-pv-health has run). */
  pvState: string | null;
}

/**
 * Fetches live system health for a single property by combining
 * energy_systems poll status with property_realtime_status.
 */
export function useSystemHealth(propertyId: string | undefined) {
  return useQuery({
    queryKey: ['system-health', propertyId],
    queryFn: async (): Promise<SystemHealthData | null> => {
      if (!propertyId) return null;

      // Fetch poll status from energy_systems (most recent primary system)
      const { data: systems } = await supabase
        .from('energy_systems')
        .select('last_poll_at, last_poll_status, last_non_empty_telemetry_at, is_active')
        .eq('property_id', propertyId)
        .eq('is_active', true)
        .order('is_primary', { ascending: false })
        .limit(1);

      // Fetch realtime status
      const { data: realtime } = await supabase
        .from('property_realtime_status')
        .select('is_curtailing, is_underperforming, pv_underperformance_score, pv_state, pv_state_reason')
        .eq('property_id', propertyId)
        .maybeSingle();

      // Fetch active alerts with notification status
      const { data: activeAlertRows } = await supabase
        .from('property_alert_state')
        .select('event_kind, last_sent_notification_id')
        .eq('property_id', propertyId)
        .eq('is_active', true);

      const system = systems?.[0];
      const isCurtailing = realtime?.is_curtailing ?? false;
      const isUnderperforming = realtime?.is_underperforming ?? false;
      // WT-341 v3: prefer the honest pv_state; a dead/partial PV property must NOT read green.
      const pvState = (realtime?.pv_state as string | null) ?? null;
      const pvTelemetryProblem = pvState === 'dead_pv_telemetry' || pvState === 'partial_inverter_outage';
      const pvUnderperforming = pvState ? pvState === 'underperforming' : isUnderperforming;
      const pvAttention = pvTelemetryProblem || pvUnderperforming;

      // Determine online/offline/stale
      const STALE_MINUTES = POLL_STALE_MINUTES;
      const OFFLINE_MINUTES = POLL_OFFLINE_MINUTES;
      let minutesSinceData: number | null = null;
      const lastDataTs = system?.last_non_empty_telemetry_at ?? system?.last_poll_at;
      if (lastDataTs) {
        minutesSinceData = Math.floor((Date.now() - new Date(lastDataTs).getTime()) / 60000);
      }

      const isOffline = !system || minutesSinceData === null || minutesSinceData > OFFLINE_MINUTES;
      const isStale = !isOffline && minutesSinceData != null && minutesSinceData > STALE_MINUTES;
      const isOnline = !isOffline && !isStale;
      const alerts = activeAlertRows ?? [];
      const hasActiveAlerts = alerts.length > 0;

      // Check if a notification was actually sent for relevant event kinds
      const notifiedKinds = new Set(
        alerts
          .filter((a) => a.last_sent_notification_id != null)
          .map((a) => a.event_kind),
      );
      const NOTIFIED_MSG = 'Your installer has been notified.';
      const VISIBLE_MSG = 'Your installer can see this in their dashboard.';

      function installerMsg(eventKinds: string[]): string {
        return eventKinds.some((k) => notifiedKinds.has(k)) ? NOTIFIED_MSG : VISIBLE_MSG;
      }

      // Classify: red / yellow / green
      if (isOffline) {
        return {
          status: 'issue',
          label: 'Issue Detected',
          explanation: `Your system appears to be offline. ${installerMsg(['system_offline'])}`,
          isOnline: false,
          isCurtailing,
          isUnderperforming,
          pvState,
        };
      }

      if (isStale || pvAttention || isCurtailing || hasActiveAlerts) {
        let explanation = 'Your installer is monitoring the situation.';
        if (pvTelemetryProblem) {
          // WT-341/345: connected but PV telemetry is dead/partial — do NOT imply the panels are underperforming.
          explanation = pvState === 'partial_inverter_outage'
            ? `One or more inverters aren't reporting solar production. ${installerMsg(['system_offline'])}`
            : `We can't read solar production from this system right now. ${installerMsg(['data_stale_low_energy', 'system_offline'])}`;
        } else if (pvUnderperforming) {
          explanation = `Solar production is lower than expected today. ${installerMsg(['resilience_risk', 'resilience_risk_critical', 'pv_curtailment_daily_digest'])}`;
        } else if (isCurtailing) {
          explanation = 'Your battery is full and some solar energy is being limited. This is normal on sunny days.';
        } else if (isStale) {
          explanation = `System data is delayed. ${installerMsg(['data_stale_low_energy'])}`;
        } else if (hasActiveAlerts) {
          explanation = `A condition is being monitored. ${installerMsg(alerts.map((a) => a.event_kind))}`;
        }

        return {
          status: 'attention',
          label: 'Needs Attention',
          explanation,
          isOnline: true,
          isCurtailing,
          isUnderperforming: pvUnderperforming,
          pvState,
        };
      }

      return {
        status: 'healthy',
        label: 'System Healthy',
        explanation: 'Your system is producing normally and all connections are active.',
        isOnline: true,
        isCurtailing: false,
        isUnderperforming: false,
        pvState,
      };
    },
    enabled: !!propertyId,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}
